import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UserFilters {
  status: string | null;
  roles: string[];
  companyIds: string[];
  profileCompleted: boolean | null;
  suspendedByCompany: boolean | null;
  joinedDateRanges: { from: string | null; to: string | null }[];
  page: number;
  pageSize: number;
}

export const DEFAULT_USER_FILTERS: UserFilters = {
  status: null,
  roles: [],
  companyIds: [],
  profileCompleted: null,
  suspendedByCompany: null,
  joinedDateRanges: [],
  page: 1,
  pageSize: 50,
};

export interface PlatformUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  personal_phone_number: string | null;
  company_position: string | null;
  profile_completed: boolean | null;
  status: string | null;
  is_active: boolean;
  company_id: string | null;
  company_name: string | null;
  admin_role: string | null;
  suspended_by_company: boolean | null;
  joined_at: string | null;
  member_status: string | null;
}

interface QueryResult {
  total: number;
  page: number;
  total_pages: number;
  rows: PlatformUser[];
}

export function useUsers(filters: UserFilters) {
  const query = useQuery({
    queryKey: ["platform-users", filters],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("query_platform_users", {
        p_status: filters.status ?? undefined,
        p_roles: filters.roles.length > 0 ? filters.roles : undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p_company_ids: filters.companyIds.length > 0 ? (filters.companyIds as any) : undefined,
        p_profile_completed: filters.profileCompleted ?? undefined,
        p_suspended_by_company: filters.suspendedByCompany ?? undefined,
        p_joined_date_ranges: filters.joinedDateRanges.length > 0
          ? filters.joinedDateRanges.map(r => ({
              from: r.from ? new Date(r.from + 'T00:00:00').toISOString() : null,
              to: r.to ? new Date(r.to + 'T23:59:59.999').toISOString() : null,
            }))
          : null,
        p_page: filters.page,
        p_page_size: filters.pageSize,
      });
      if (error) throw error;
      return (data as unknown as QueryResult) ?? null;
    },
  });

  const result = query.data ?? null;
  return {
    users: (result?.rows ?? []) as PlatformUser[],
    total: result?.total ?? 0,
    totalPages: result?.total_pages ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}
